import { type CSSProperties, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  NavIcon,
  useBrandTokens,
  orgLogoHorizontalUrl,
  orgLogoUrl,
  sidebarLogoMaxWidth,
  sidebarLogoMaxHeight,
  sidebarLogoObjectPosition,
} from '../shell/shared';
import { BookAppointmentDrawer } from './appointments/BookAppointmentDrawer';
import { PatientsServedDrawer } from './home-dashboard/PatientsServedDrawer';
import { InQueueDrawer } from './home-dashboard/InQueueDrawer';
import { CollectionsDrawer } from './home-dashboard/CollectionsDrawer';
import { OutstandingDrawer } from './home-dashboard/OutstandingDrawer';
import { NewOutstandingTodayDrawer } from './home-dashboard/NewOutstandingTodayDrawer';
import { PatientAlertsDrawer } from './home-dashboard/PatientAlertsDrawer';
import { usePowerSyncQuery, usePowerSyncDb } from '../adapters/powersync';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { useAuth, usePermissions } from '@helios/auth-firebase-web';
import {
  useProviderScope,
  scopedSpec,
  isProviderLinkMissing,
  ProviderLinkMissingNotice,
} from '../auth/useProviderScope';
import { ensureVisitThenCreateEncounter } from '../services/encounter-service';
import {
  TOP_ACTIONS,
  MONEY_TOGGLE,
  KPIS,
  ALERTS_KPI,
  WIDGETS,
  START_ENCOUNTER,
  isElementVisible,
  visibleElements,
  visibleIds,
} from './home/homeSections';
import {
  qHomePatientsServedToday,
  qHomeQueueCounts,
  qHomeCollectionsToday,
  qHomeNewOutstandingToday,
  qHomeOutstanding,
  qHomeAlerts,
  qHomeQueueSnapshot,
  qHomeScheduleToday,
  qHomeRecentPatients,
  qHomeBillingDetails,
  qPharmacyQueueSummary,
  type HomePatientsServedRow,
  type HomeQueueCountRow,
  type HomeCollectionsRow,
  type HomeNewOutstandingTodayRow,
  type HomeOutstandingRow,
  type HomeAlertRow,
  type HomeQueueEntryRow,
  type HomeScheduleRow,
  type HomeRecentPatientRow,
  type HomeBillingDetailRow,
  type PharmacyQueueSummaryRow,
} from '@helios/data-access';

// ─── Helpers ───

function todayIso(): string {
  // LOCAL date (not UTC). `queue_date` and appointment dates are stored as the
  // clinic's local YYYY-MM-DD (see QueuePage), so using toISOString() here (UTC)
  // made the Home queue/schedule disagree with the Queue page around midnight in
  // non-UTC timezones — Home would show entries the Queue page hid, or vice versa.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Compute today's day boundaries in user timezone, returned as UTC ISO strings. */
function todayBounds(): { dayStartUtc: string; dayEndUtc: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return { dayStartUtc: start.toISOString(), dayEndUtc: end.toISOString() };
}

function fmtTime(t: string | null): string {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${String(h % 12 || 12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0)
    return `Today, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function patientStatus(qs: string | null, es: string | null): string {
  if (qs && ['waiting', 'called', 'serving'].includes(qs)) return 'In Queue';
  if (es === 'open') return 'In Consult';
  return 'Completed';
}

// ─── Status chip colors ───

const SC: Record<string, { bg: string; color: string }> = {
  done: { bg: '#dcfce7', color: '#16a34a' },
  serving: { bg: '#dbeafe', color: '#2563eb' },
  waiting: { bg: '#fefce8', color: '#ca8a04' },
  called: { bg: '#fff7ed', color: '#ea580c' },
  next: { bg: '#dc2626', color: '#fff' },
  upcoming: { bg: '#f3f4f6', color: '#6b7280' },
  not_queued: { bg: '#fef2f2', color: '#dc2626' },
  'In Consult': { bg: '#dbeafe', color: '#2563eb' },
  Completed: { bg: '#dcfce7', color: '#16a34a' },
  'In Queue': { bg: '#fefce8', color: '#ca8a04' },
  'Follow-up': { bg: '#f3e8ff', color: '#7c3aed' },
};

const SL: Record<string, string> = {
  done: '✓ Done',
  serving: 'Serving',
  waiting: 'Waiting',
  called: 'Called',
  not_queued: 'Not Queued',
};

// ─── KPI Tile ───

function KpiTile({
  icon,
  value,
  label,
  sub,
  color,
  iconColor,
  onClick,
}: {
  icon: string;
  value: string;
  label: string;
  sub: string;
  color: string;
  iconColor: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: '1 1 180px',
        minWidth: 160,
        padding: 'var(--sp-4)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-md)',
          background: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          marginBottom: 'var(--sp-3)',
        }}
      >
        <NavIcon name={icon} />
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-xl, 24px)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: iconColor, marginTop: 2 }}>{sub}</div>
      {onClick && (
        <div style={{ position: 'absolute', top: 16, right: 16, color: 'var(--text-placeholder)' }}>
          <NavIcon name="chevron-right" />
        </div>
      )}
    </div>
  );
}

// ─── Queue Snapshot ───

function QueueSnapshotWidget({
  entries,
  onOpenQueue,
  onOpenPatient,
  onCallNext,
}: {
  entries: HomeQueueEntryRow[];
  onOpenQueue: () => void;
  onOpenPatient: (patientId: string) => void;
  onCallNext: () => void;
}) {
  const called = entries.filter((e) => e.status === 'called');
  const serving = entries.filter((e) => e.status === 'serving');
  const waiting = entries.filter((e) => e.status === 'waiting');

  const numStyle = (c: string): CSSProperties => ({
    minWidth: 48,
    height: 28,
    padding: '0 8px',
    borderRadius: 'var(--radius-md)',
    background: c,
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  });

  const row = (e: HomeQueueEntryRow, bg: string, border: string, numColor: string) => (
    <div
      key={e.id}
      role="button"
      tabIndex={0}
      onClick={() => onOpenPatient(e.patient_id)}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          onOpenPatient(e.patient_id);
        }
      }}
      title={`Open ${e.patient_name ?? 'patient'}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        background: bg,
        border: `1px solid ${border}`,
        cursor: 'pointer',
      }}
    >
      <div style={numStyle(numColor)}>Q-{String(e.queue_number ?? 0).padStart(3, '0')}</div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {e.patient_name ?? 'Unknown'}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {e.service_name ?? '—'}
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-full)',
              background: '#ecfdf5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#059669',
            }}
          >
            <NavIcon name="list-ordered" />
          </div>
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Queue Snapshot
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Live patient queue
            </div>
          </div>
        </div>
        <button
          onClick={onCallNext}
          disabled={waiting.length === 0}
          title={
            waiting.length === 0 ? 'No waiting patients to call' : 'Call the next waiting patient'
          }
          style={{
            height: 30,
            padding: '0 14px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: waiting.length === 0 ? 'var(--border)' : '#16a34a',
            color: '#fff',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: waiting.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          Call Next
        </button>
      </div>

      {called.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 8,
            }}
          >
            📣 NOW CALLING
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {called.map((e) => row(e, '#fffbeb', '#fde68a', '#f59e0b'))}
          </div>
        </div>
      )}

      {serving.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 8,
            }}
          >
            ⚡ NOW SERVING
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {serving.map((e) => row(e, '#f0fdf4', '#bbf7d0', '#16a34a'))}
          </div>
        </div>
      )}

      {waiting.length > 0 && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 8,
            }}
          >
            <NavIcon name="users" /> WAITING — {waiting.length} patients
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {waiting.map((e) => row(e, '#f9fafb', '#e5e7eb', '#6b7280'))}
          </div>
        </div>
      )}

      {entries.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: 'var(--text-placeholder)',
            fontSize: 'var(--font-size-sm)',
            padding: 'var(--sp-4)',
          }}
        >
          No patients in queue
        </div>
      )}

      <button
        onClick={onOpenQueue}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
          padding: 0,
          textAlign: 'center',
        }}
      >
        Open Queue &gt;
      </button>
    </div>
  );
}

// ─── Currently Serving ───

function CurrentlyServingWidget({
  queueEntries,
  primary,
  onOpenPatient,
  onOpenEncounter,
}: {
  queueEntries: HomeQueueEntryRow[];
  primary: string;
  onOpenPatient?: (patientId: string) => void;
  onOpenEncounter?: (patientId: string) => void;
}) {
  // Most recent patient moved to 'serving' (snapshot query orders serving first).
  const serving = useMemo(
    () => queueEntries.find((e) => e.status === 'serving') ?? null,
    [queueEntries],
  );

  if (!serving) {
    return (
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          background: 'linear-gradient(135deg, #bbf7d0 0%, #86efac 100%)',
          padding: 'var(--sp-4)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: '#052e16',
          }}
        >
          Currently Serving
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: '#166534',
            marginTop: 'var(--sp-3)',
            padding: 'var(--sp-4)',
          }}
        >
          No patient being served
        </div>
      </div>
    );
  }

  const initials = (serving.patient_name ?? '??')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const canOpen = !!serving.patient_id;

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        background: 'linear-gradient(135deg, #bbf7d0 0%, #86efac 100%)',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-full)',
            background: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#166534',
          }}
        >
          <NavIcon name="activity" />
        </div>
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: '#052e16',
            }}
          >
            Currently Serving
          </div>
          <div style={{ fontSize: 10, color: '#166534' }}>In progress</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div
          style={{
            minWidth: 48,
            height: 28,
            padding: '0 8px',
            borderRadius: 'var(--radius-md)',
            background: '#166534',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          Q-{String(serving.queue_number ?? 0).padStart(3, '0')}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: '#166534' }}>In progress</div>
      </div>
      <div
        role={canOpen && onOpenPatient ? 'button' : undefined}
        tabIndex={canOpen && onOpenPatient ? 0 : undefined}
        onClick={() => canOpen && onOpenPatient?.(serving.patient_id)}
        onKeyDown={(ev) => {
          if ((ev.key === 'Enter' || ev.key === ' ') && canOpen && onOpenPatient) {
            ev.preventDefault();
            onOpenPatient(serving.patient_id);
          }
        }}
        title={canOpen && onOpenPatient ? `Open ${serving.patient_name ?? 'patient'}` : undefined}
        style={{
          background: 'rgba(255,255,255,0.7)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          cursor: canOpen && onOpenPatient ? 'pointer' : 'default',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-full)',
              background: '#bbf7d0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: '#166534',
            }}
          >
            {initials}
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {serving.patient_name ?? 'Unknown'}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text)',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <NavIcon name="clipboard" /> {serving.service_name ?? '—'}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        <button
          onClick={() => canOpen && onOpenEncounter?.(serving.patient_id)}
          disabled={!canOpen}
          style={{
            flex: 1,
            height: 36,
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: primary,
            color: '#fff',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: canOpen ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          <NavIcon name="file-text" /> Open Encounter
        </button>
      </div>
    </div>
  );
}

// ─── Next Patient ───

function NextPatientWidget({
  queueEntries,
  primary,
  onStartEncounter,
  onOpenPatient,
  canStart = true,
}: {
  queueEntries: HomeQueueEntryRow[];
  primary: string;
  onStartEncounter?: (patientId: string) => void;
  onOpenPatient?: (patientId: string) => void;
  // PRD.269 — "Start Encounter" is a clinical edit action (Consultation edit).
  canStart?: boolean;
}) {
  // First waiting patient in queue order = next patient
  const next = useMemo(() => {
    return queueEntries.find((e) => e.status === 'waiting') ?? null;
  }, [queueEntries]);

  if (!next) {
    return (
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
          padding: 'var(--sp-4)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: '#431407',
          }}
        >
          My Next Patient
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: '#78350f',
            marginTop: 'var(--sp-3)',
            padding: 'var(--sp-4)',
          }}
        >
          No patients waiting in queue
        </div>
      </div>
    );
  }

  const initials = (next.patient_name ?? '??')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-full)',
            background: 'rgba(255,255,255,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9a3412',
          }}
        >
          <NavIcon name="user" />
        </div>
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: '#431407',
            }}
          >
            My Next Patient
          </div>
          <div style={{ fontSize: 10, color: '#78350f' }}>Next in queue</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div
          style={{
            minWidth: 48,
            height: 28,
            padding: '0 8px',
            borderRadius: 'var(--radius-md)',
            background: '#78350f',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          Q-{String(next.queue_number ?? 0).padStart(3, '0')}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: '#78350f' }}>
          Position #{queueEntries.filter((e) => e.status === 'waiting').indexOf(next) + 1} in queue
        </div>
      </div>
      <div
        role={next.patient_id && onOpenPatient ? 'button' : undefined}
        tabIndex={next.patient_id && onOpenPatient ? 0 : undefined}
        onClick={() => next.patient_id && onOpenPatient?.(next.patient_id)}
        onKeyDown={(ev) => {
          if ((ev.key === 'Enter' || ev.key === ' ') && next.patient_id && onOpenPatient) {
            ev.preventDefault();
            onOpenPatient(next.patient_id);
          }
        }}
        title={
          next.patient_id && onOpenPatient ? `Open ${next.patient_name ?? 'patient'}` : undefined
        }
        style={{
          background: 'rgba(255,255,255,0.7)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          cursor: next.patient_id && onOpenPatient ? 'pointer' : 'default',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-full)',
              background: '#fecaca',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: '#991b1b',
            }}
          >
            {initials}
          </div>
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              {next.patient_name ?? 'Unknown'}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text)',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <NavIcon name="clipboard" /> {next.service_name ?? '—'}
          </span>
        </div>
      </div>
      {canStart && (
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <button
            onClick={() => next.patient_id && onStartEncounter?.(next.patient_id)}
            style={{
              flex: 1,
              height: 36,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
            }}
          >
            <NavIcon name="activity" /> Start Encounter
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Schedule ───

function ScheduleWidget({
  schedule,
  queueEntries,
  onViewDay,
  onOpenCalendar,
  onOpenPatient,
}: {
  schedule: HomeScheduleRow[];
  queueEntries: HomeQueueEntryRow[];
  onViewDay: () => void;
  onOpenCalendar?: () => void;
  onOpenPatient: (patientId: string) => void;
}) {
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();

  // Build a map of appointment_id → queue status for quick lookup
  // queueEntries don't have appointment_id, so we match by patient_id instead
  const queueByPatient = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of queueEntries) {
      map.set(q.patient_id, q.status);
    }
    return map;
  }, [queueEntries]);

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-full)',
              background: '#dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2563eb',
            }}
          >
            <NavIcon name="calendar" />
          </div>
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              My Schedule (Today)
            </div>
          </div>
        </div>
        <button
          onClick={onViewDay}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          View Day <NavIcon name="chevron-right" />
        </button>
        {onOpenCalendar && (
          <button
            onClick={onOpenCalendar}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontFamily: 'var(--font-family)',
            }}
          >
            Open Calendar <NavIcon name="chevron-right" />
          </button>
        )}
      </div>
      {schedule.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: 'var(--text-placeholder)',
            fontSize: 'var(--font-size-sm)',
            padding: 'var(--sp-4)',
          }}
        >
          No appointments today
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {schedule.map((s, i) => {
          const [h, m] = (s.start_time ?? '0:0').split(':').map(Number);
          const apptMins = h * 60 + m;
          const endMins = apptMins + (s.duration_minutes ?? 30);
          const queueStatus = s.patient_id ? queueByPatient.get(s.patient_id) : undefined;

          let status: string;
          if (queueStatus === 'done') status = 'done';
          else if (queueStatus === 'serving') status = 'serving';
          else if (queueStatus === 'called') status = 'called';
          else if (queueStatus === 'waiting') status = 'waiting';
          else if (nowMins >= endMins) status = 'done';
          else status = 'not_queued';
          const sc = SC[status] ?? { bg: '#f3f4f6', color: '#6b7280' };
          const label = SL[status];
          const openPatient = s.patient_id
            ? () => onOpenPatient(s.patient_id as string)
            : undefined;
          return (
            <div
              key={s.id}
              role={openPatient ? 'button' : undefined}
              tabIndex={openPatient ? 0 : undefined}
              onClick={openPatient}
              onKeyDown={
                openPatient
                  ? (ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        openPatient();
                      }
                    }
                  : undefined
              }
              title={openPatient ? `Open ${s.patient_name ?? 'patient'}` : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: '10px 0',
                borderBottom: i < schedule.length - 1 ? '1px solid var(--border)' : 'none',
                cursor: openPatient ? 'pointer' : 'default',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  width: 70,
                  flexShrink: 0,
                }}
              >
                {fmtTime(s.start_time)}
              </span>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {s.patient_name ?? 'Unknown'}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {s.service_name ?? '—'}
                </div>
              </div>
              {label && (
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    padding: '2px 10px',
                    borderRadius: 'var(--radius-full)',
                    background: sc.bg,
                    color: sc.color,
                    fontWeight: 'var(--font-weight-semi)',
                  }}
                >
                  {label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Billing Snapshot ───

function BillingSnapshotWidget({
  outstanding,
  collections,
  details,
  hideMoney,
}: {
  outstanding: { totalCents: number; count: number };
  collections: { totalCents: number; count: number };
  details: HomeBillingDetailRow | null;
  /** PRD.248 — when true, monetary amounts render as `***` (counts stay visible). */
  hideMoney: boolean;
}) {
  const fmtCompact = useFormatMoney({ compact: true });
  // Home dashboard money inputs use a loose "cents-or-major" convention
  // (queries return cents but historically went through a divide-by-100-free
  // formatter). Preserve the original behavior: treat values as already
  // major and round to whole units via the compact formatter.
  const fmt = (amount: number) => (hideMoney ? '***' : fmtCompact(Math.round(amount)));
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-full)',
            background: '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#d97706',
          }}
        >
          <NavIcon name="credit-card" />
        </div>
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Billing Snapshot
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Financial overview
          </div>
        </div>
      </div>
      <div
        style={{
          padding: 'var(--sp-4)',
          borderRadius: 'var(--radius-md)',
          background: '#fef2f2',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Unpaid Invoices
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-xl, 24px)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {fmt(outstanding.totalCents)}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: '#dc2626' }}>
            {outstanding.count} invoices
          </div>
        </div>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-full)',
            background: '#fecaca',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#dc2626',
          }}
        >
          <NavIcon name="alert-triangle" />
        </div>
      </div>
      <div
        style={{
          padding: 'var(--sp-4)',
          borderRadius: 'var(--radius-md)',
          background: '#f0fdf4',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Payments Today
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-xl, 24px)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {fmt(collections.totalCents)}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: '#16a34a' }}>
            {collections.count} payments
          </div>
        </div>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-full)',
            background: '#bbf7d0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#16a34a',
          }}
        >
          <NavIcon name="credit-card" />
        </div>
      </div>
      {details && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          {details.oldest_invoice_no && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Oldest unpaid:</span>
              <span
                style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-medium)' }}
              >
                {details.oldest_invoice_no} ({details.oldest_days ?? 0} days)
              </span>
            </div>
          )}
          {details.top_patient_name && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Top unpaid patient:</span>
              <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
                {details.top_patient_name}
              </span>
            </div>
          )}
        </div>
      )}
      <button
        onClick={() => console.log('View Billing')}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
          padding: 0,
          textAlign: 'center',
        }}
      >
        View Billing &gt;
      </button>
    </div>
  );
}

// ─── Recent Patients ───

function RecentPatientsWidget({
  patients,
  onViewAll,
  onOpenPatient,
}: {
  patients: HomeRecentPatientRow[];
  onViewAll: () => void;
  onOpenPatient: (patientId: string) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-full)',
              background: '#f3e8ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#7c3aed',
            }}
          >
            <NavIcon name="users" />
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Recent Patients
          </div>
        </div>
        <button
          onClick={onViewAll}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          View Patients <NavIcon name="chevron-right" />
        </button>
      </div>
      {patients.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: 'var(--text-placeholder)',
            fontSize: 'var(--font-size-sm)',
            padding: 'var(--sp-4)',
          }}
        >
          No recent patients
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {patients.map((p, i) => {
          const status = patientStatus(p.queue_status, p.encounter_status);
          const sc = SC[status] ?? { bg: '#f3f4f6', color: '#6b7280' };
          return (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpenPatient(p.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenPatient(p.id);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: '8px var(--sp-2)',
                margin: '0 calc(-1 * var(--sp-2))',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                borderBottom: i < patients.length - 1 ? '1px solid var(--border)' : 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg-surface-2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {p.full_name ?? 'Unknown'}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {fmtRelative(p.last_visited_at)}
                </div>
              </div>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-full)',
                  background: sc.bg,
                  color: sc.color,
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                {status}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ───

type KpiDrawer =
  | 'patients-served'
  | 'in-queue'
  | 'collections'
  | 'new-outstanding'
  | 'outstanding'
  | 'alerts'
  | null;

export function HomeDashboardPage() {
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const today = useMemo(todayIso, []);
  const { dayStartUtc, dayEndUtc } = useMemo(todayBounds, []);
  const [openDrawer, setOpenDrawer] = useState<KpiDrawer>(null);
  // Shared "Book a Patient" drawer, opened in place from the top action.
  const [bookOpen, setBookOpen] = useState(false);
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  // PRD.269 — same permission resolver as PRD.268 route/nav gating.
  const { canAccess } = usePermissions();
  // PRD.293 — scope clinical widgets to a doctor's own provider; operational
  // users (and the financial widgets) stay clinic-wide.
  const providerScope = useProviderScope();
  const fmtCompact = useFormatMoney({ compact: true });
  const fmt = (amount: number) => fmtCompact(Math.round(amount));

  // PRD.248 — Homepage monetary-value privacy. Sensitive money figures are
  // hidden by default (the Homepage may be visible to patients) and revealed
  // intentionally via a single page-level toggle. Non-monetary counts are
  // never masked. `***` is shown when hidden so a value is clearly concealed,
  // not missing.
  const [moneyHidden, setMoneyHidden] = useState(true);
  const fmtMoney = (amount: number) => (moneyHidden ? '***' : fmt(amount));

  const handleStartEncounter = useCallback(
    async (patientId: string, appointmentId: string) => {
      if (!db || !orgId) return;
      try {
        const { encounterId } = await ensureVisitThenCreateEncounter({
          db,
          orgId,
          clinicId: null,
          patientId,
          appointmentId,
        });
        navigate(`/patient/${patientId}/encounters/${encounterId}`);
      } catch (err) {
        console.error('[Home] Start Encounter failed:', err);
      }
    },
    [db, orgId, navigate],
  );

  // Open the patient's existing latest encounter (don't create a new one, since
  // a serving patient already has one). Falls back to the encounters list.
  const handleOpenEncounter = useCallback(
    async (patientId: string) => {
      if (!db) {
        navigate(`/patient/${patientId}/encounters`);
        return;
      }
      try {
        const rows = await db.getAll<{ id: string }>(
          `SELECT id FROM patient_encounters WHERE patient_id = ? ORDER BY created_at DESC LIMIT 1`,
          [patientId],
        );
        if (rows[0]) navigate(`/patient/${patientId}/encounters/${rows[0].id}`);
        else navigate(`/patient/${patientId}/encounters`);
      } catch (err) {
        console.error('[Home] Open Encounter failed:', err);
        navigate(`/patient/${patientId}/encounters`);
      }
    },
    [db, navigate],
  );

  // ── Queries ──
  const { data: servedRows = [] } = usePowerSyncQuery<HomePatientsServedRow>(
    scopedSpec(providerScope, 'home-served', (pid) =>
      qHomePatientsServedToday(dayStartUtc, dayEndUtc, pid),
    ),
  );
  const { data: queueCountRows = [] } = usePowerSyncQuery<HomeQueueCountRow>(
    scopedSpec(providerScope, 'home-queue-counts', (pid) => qHomeQueueCounts(today, pid)),
  );
  const { data: collectionsRows = [] } = usePowerSyncQuery<HomeCollectionsRow>(
    qHomeCollectionsToday(dayStartUtc, dayEndUtc),
  );
  // PRD.250 — New Outstanding Today: unpaid portion of charges created today.
  const { data: newOutstandingRows = [] } = usePowerSyncQuery<HomeNewOutstandingTodayRow>(
    qHomeNewOutstandingToday(dayStartUtc, dayEndUtc),
  );
  const { data: outstandingRows = [] } = usePowerSyncQuery<HomeOutstandingRow>(qHomeOutstanding());
  const { data: alertRows = [] } = usePowerSyncQuery<HomeAlertRow>(qHomeAlerts());
  const { data: queueEntries = [] } = usePowerSyncQuery<HomeQueueEntryRow>(
    scopedSpec(providerScope, 'home-queue-snapshot', (pid) => qHomeQueueSnapshot(today, pid)),
  );

  // Call Next — mark the earliest waiting queue entry as 'called'. Mirrors the
  // Queue page's callNext write (status → 'called' + called_at). The snapshot
  // query orders waiting by position, so the first waiting entry is the next.
  const handleCallNext = useCallback(async () => {
    if (!db) return;
    const nextWaiting = queueEntries.find((e) => e.status === 'waiting');
    if (!nextWaiting) return;
    const now = new Date().toISOString();
    try {
      await db.execute(
        `UPDATE queue_entries SET status = 'called', called_at = ?, updated_at = ? WHERE id = ?`,
        [now, now, nextWaiting.id],
      );
    } catch (err) {
      console.error('[home] call next failed', err);
    }
  }, [db, queueEntries]);
  const { data: scheduleRows = [] } = usePowerSyncQuery<HomeScheduleRow>(
    scopedSpec(providerScope, 'home-schedule', (pid) => qHomeScheduleToday(today, pid)),
  );
  const { data: recentPatients = [] } = usePowerSyncQuery<HomeRecentPatientRow>(
    qHomeRecentPatients(userId),
  );
  const { data: billingDetailRows = [] } =
    usePowerSyncQuery<HomeBillingDetailRow>(qHomeBillingDetails());
  const { data: pharmacySummaryRows = [] } =
    usePowerSyncQuery<PharmacyQueueSummaryRow>(qPharmacyQueueSummary());

  // ── Derived KPIs ──
  const served = servedRows[0]?.count ?? 0;
  const qc = queueCountRows[0];
  const queueTotal = (qc?.waiting ?? 0) + (qc?.called ?? 0) + (qc?.serving ?? 0);
  const coll = collectionsRows[0];
  const newOut = newOutstandingRows[0];
  const out = outstandingRows[0];
  const alerts = alertRows[0];
  const pharm = pharmacySummaryRows[0];
  const pharmacyReady = pharm?.ready_count ?? 0;
  const pharmacyPartial = pharm?.partial_count ?? 0;
  const pharmacyPatients = pharm?.patient_count ?? 0;
  const pharmacySub =
    pharmacyReady === 0
      ? 'No medicines ready'
      : `${pharmacyPartial} partial • ${pharmacyPatients} patient${
          pharmacyPatients === 1 ? '' : 's'
        }`;

  // ── KPI display objects keyed by id (PRD.269 gates by id via homeSections) ──
  type KpiProps = {
    icon: string;
    value: string;
    label: string;
    sub: string;
    color: string;
    iconColor: string;
    onClick?: () => void;
  };
  const kpiById: Record<string, KpiProps> = {
    'patients-served': {
      icon: 'users',
      value: String(served),
      label: 'Patients Served Today',
      sub: 'Includes walk-ins',
      color: '#f0fdf4',
      iconColor: '#16a34a',
      onClick: () => setOpenDrawer('patients-served'),
    },
    'in-queue': {
      icon: 'list-ordered',
      value: String(queueTotal),
      label: 'In Queue Now',
      sub: `${qc?.waiting ?? 0}W • ${qc?.called ?? 0}C • ${qc?.serving ?? 0}S`,
      color: '#fefce8',
      iconColor: '#ca8a04',
      onClick: () => setOpenDrawer('in-queue'),
    },
    collections: {
      icon: 'credit-card',
      value: fmtMoney(coll?.total_cents ?? 0),
      label: 'Collections Today',
      sub: `${coll?.payment_count ?? 0} payments`,
      color: '#ecfdf5',
      iconColor: '#059669',
      onClick: () => setOpenDrawer('collections'),
    },
    // PRD.250 — New Outstanding Today: today-created charges still unpaid.
    'new-outstanding': {
      icon: 'clock',
      value: fmtMoney(newOut?.total_cents ?? 0),
      label: 'New Outstanding Today',
      sub: `${newOut?.invoice_count ?? 0} new unpaid`,
      color: '#fffbeb',
      iconColor: '#d97706',
      onClick: () => setOpenDrawer('new-outstanding'),
    },
    // PRD.200 — Pharmacy summary; clicking navigates to the global Pharmacy page.
    'ready-to-dispense': {
      icon: 'heart',
      value: String(pharmacyReady),
      label: 'Ready to Dispense',
      sub: pharmacySub,
      color: '#eef2ff',
      iconColor: '#4f46e5',
      onClick: () => navigate('/inventory/pharmacy'),
    },
  };

  // ── Permission-gated visibility (PRD.269) ──
  const visibleKpis = visibleElements(KPIS, canAccess).map((k) => kpiById[k.id]);
  const visibleWidgetIds = visibleIds(WIDGETS, canAccess);
  const topActions = visibleElements(TOP_ACTIONS, canAccess);
  const showMoneyToggle = isElementVisible(MONEY_TOGGLE, canAccess);
  const showAlerts = isElementVisible(ALERTS_KPI, canAccess);
  const canStartEncounter = isElementVisible(START_ENCOUNTER, canAccess);

  // Patient Alerts rendered as a KpiTile in the right rail so its styling,
  // drawer behavior, and copy are unchanged.
  const patientAlertsKpi = {
    icon: 'alert-triangle',
    value: String(alerts?.total ?? 0),
    label: 'Patient Alerts',
    sub: `${alerts?.critical ?? 0} critical • ${alerts?.warning ?? 0} warning`,
    color: '#fef2f2',
    iconColor: '#dc2626',
    onClick: () => setOpenDrawer('alerts'),
  };

  const sectionHeaderStyle: CSSProperties = {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semi)',
    lineHeight: 'var(--line-height-base)',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
    margin: '0 0 var(--sp-3)',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-5)',
        maxWidth: 1440,
        margin: '0 auto',
        width: '100%',
      }}
    >
      {/* PRD.293 — scoped doctor with no provider link: fail closed. */}
      {isProviderLinkMissing(providerScope) && <ProviderLinkMissingNotice />}

      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          {(orgLogoHorizontalUrl || orgLogoUrl) && (
            <img
              src={orgLogoHorizontalUrl || orgLogoUrl}
              alt=""
              style={
                orgLogoHorizontalUrl
                  ? {
                      height: 52,
                      maxWidth: sidebarLogoMaxWidth ?? 220,
                      maxHeight: sidebarLogoMaxHeight,
                      objectFit: 'contain',
                      objectPosition: sidebarLogoObjectPosition,
                      display: 'block',
                    }
                  : {
                      width: 48,
                      height: 48,
                      borderRadius: 'var(--radius-full)',
                      objectFit: 'cover',
                      display: 'block',
                    }
              }
            />
          )}
          <p
            style={{
              margin: '6px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Today's overview
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          {/* PRD.248 — page-level money privacy toggle. PRD.269 — only shown
              when the user has billing visibility (otherwise nothing to hide). */}
          {showMoneyToggle && (
            <button
              onClick={() => setMoneyHidden((v) => !v)}
              title={moneyHidden ? 'Show amounts' : 'Hide amounts'}
              aria-label={moneyHidden ? 'Show monetary amounts' : 'Hide monetary amounts'}
              aria-pressed={!moneyHidden}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name={moneyHidden ? 'eye-off' : 'eye'} />
              {moneyHidden ? 'Show amounts' : 'Hide amounts'}
            </button>
          )}
          {/* PRD.269 — permission-gated top actions (Create Patient / Book a
              Patient hidden without the required edit access). */}
          {topActions.map((a) => (
            <button
              key={a.id}
              onClick={() => (a.id === 'book-patient' ? setBookOpen(true) : navigate(a.path))}
              style={{
                height: 38,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: a.primary ? 'none' : '1px solid var(--border)',
                background: a.primary ? primary : 'var(--bg-surface)',
                color: a.primary ? '#fff' : 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: a.primary ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name={a.icon} /> {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Row (permission-gated) */}
      {visibleKpis.length > 0 && (
        <div className="home-kpi-row">
          {visibleKpis.map((kpi) => (
            <KpiTile key={kpi.label} {...kpi} />
          ))}
        </div>
      )}

      {/* Main dashboard: widgets beside a right rail (Alerts + Clinic Hub).
          Responsive 3→2→1 columns via .home-* classes. All sections gated. */}
      {(visibleWidgetIds.size > 0 || showAlerts) && (
        <div className="home-main-grid">
          {/* Stack A — operational (Queue Snapshot, Billing Snapshot) */}
          {(visibleWidgetIds.has('queue-snapshot') || visibleWidgetIds.has('billing-snapshot')) && (
            <div className="home-col">
              {visibleWidgetIds.has('queue-snapshot') && (
                <QueueSnapshotWidget
                  entries={queueEntries}
                  onOpenQueue={() => navigate('/clinic/queue')}
                  onOpenPatient={(pid) => navigate(`/patient/${pid}/encounters`)}
                  onCallNext={handleCallNext}
                />
              )}
              {visibleWidgetIds.has('billing-snapshot') && (
                <BillingSnapshotWidget
                  outstanding={{
                    totalCents: out?.total_cents ?? 0,
                    count: out?.invoice_count ?? 0,
                  }}
                  collections={{
                    totalCents: coll?.total_cents ?? 0,
                    count: coll?.payment_count ?? 0,
                  }}
                  details={billingDetailRows[0] ?? null}
                  hideMoney={moneyHidden}
                />
              )}
            </div>
          )}

          {/* Stack B — patient flow (Next Patient, Schedule) */}
          {(visibleWidgetIds.has('next-patient') || visibleWidgetIds.has('schedule')) && (
            <div className="home-col">
              {visibleWidgetIds.has('next-patient') && (
                <CurrentlyServingWidget
                  queueEntries={queueEntries}
                  primary={primary}
                  onOpenPatient={(pid) => navigate(`/patient/${pid}/encounters`)}
                  onOpenEncounter={handleOpenEncounter}
                />
              )}
              {visibleWidgetIds.has('next-patient') && (
                <NextPatientWidget
                  queueEntries={queueEntries}
                  primary={primary}
                  canStart={canStartEncounter}
                  onStartEncounter={(pid) => handleStartEncounter(pid, '')}
                  onOpenPatient={(pid) => navigate(`/patient/${pid}/encounters`)}
                />
              )}
              {visibleWidgetIds.has('schedule') && (
                <div className="home-grow">
                  <ScheduleWidget
                    schedule={scheduleRows}
                    queueEntries={queueEntries}
                    onViewDay={() => navigate('/appointments/day')}
                    onOpenCalendar={() => navigate('/appointments/month')}
                    onOpenPatient={(pid) => navigate(`/patient/${pid}/encounters`)}
                  />
                </div>
              )}
            </div>
          )}

          {(showAlerts || visibleWidgetIds.has('recent-patients')) && (
            <div className="home-rail">
              {showAlerts && (
                <div>
                  <h2 style={sectionHeaderStyle}>Operational Signals</h2>
                  <KpiTile {...patientAlertsKpi} />
                </div>
              )}
              {/* Recent Patients sits below Operational Signals in the rail. */}
              {visibleWidgetIds.has('recent-patients') && (
                <div className="home-grow">
                  <RecentPatientsWidget
                    patients={recentPatients}
                    onViewAll={() => navigate('/patients')}
                    onOpenPatient={(pid) => navigate(`/patient/${pid}/encounters`)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* KPI Drawers */}
      <PatientsServedDrawer
        open={openDrawer === 'patients-served'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        dayStartUtc={dayStartUtc}
        dayEndUtc={dayEndUtc}
        portalStyle={brandTokens as CSSProperties}
      />
      <InQueueDrawer
        open={openDrawer === 'in-queue'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        today={today}
        portalStyle={brandTokens as CSSProperties}
      />
      <CollectionsDrawer
        open={openDrawer === 'collections'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        dayStartUtc={dayStartUtc}
        dayEndUtc={dayEndUtc}
        portalStyle={brandTokens as CSSProperties}
      />
      <NewOutstandingTodayDrawer
        open={openDrawer === 'new-outstanding'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        dayStartUtc={dayStartUtc}
        dayEndUtc={dayEndUtc}
        portalStyle={brandTokens as CSSProperties}
      />
      <OutstandingDrawer
        open={openDrawer === 'outstanding'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        portalStyle={brandTokens as CSSProperties}
      />
      <PatientAlertsDrawer
        open={openDrawer === 'alerts'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        alertCounts={{ critical: alerts?.critical ?? 0, warning: alerts?.warning ?? 0 }}
        portalStyle={brandTokens as CSSProperties}
      />

      {/* Book a Patient — same shared drawer used by the Appointments page,
          opened in place from the top action. */}
      <BookAppointmentDrawer open={bookOpen} onOpenChange={setBookOpen} brandTokens={brandTokens} />
    </div>
  );
}
